import { z } from "zod";

/**
 * `DEALER-PREQUAL-CONTRACT` (`P1-SIM-016`): **the dealer's capacity estimate, « Combien le client peut-il financer ? »**, one definition for the door
 * (`POST /api/v1/me/capacity-estimate`, a later PR) and for the dealer client. Contract only: no door answers it yet.
 *
 * **A pure computation.** The door stores nothing, emits no event and logs no amount; the salary is not kept (architect's ruling 9). Every figure is served
 * and drawn verbatim, the browser computes nothing (D-1020-7); every `*Amount` is whole FCFA, a `*Pct` is a percentage.
 *
 * **The bounds, by the A to H architect's rulings of 31 October.**
 * 1. Deposit 10 to 50 % and duration 24 to 60 months are the CLIENT's limits (D-1031-4, point 4), not `simulation-request.ts`'s 6 to 60 nor its 0 to 100.
 * 2. The rate is a RANGE, best grade to worst across the active panel, as the counter's monthly figure computes it.
 * 3. The monthly ceiling is the BANK's 40 % (`capacite.ts`), never Hakili's 50 %: one platform never says yes and no.
 * 4. Fees are not financed in the estimate; an assumption line and the caveat say so.
 * 5. A car is « within the range » only if at least one active bank's `vehicleAgeMaxYears` accepts it.
 * 6. `caveat` is the record's estimate notice, served, one sentence for both platforms.
 * 7. Two states beyond the figures: « no capacity » and « no car within the range » (an empty park reads `parkCount: 0`).
 */

/** The client's slider limits (D-1031-4, point 4), echoed on every answer as `limits` so a screen draws what is served. */
export const CAPACITY_DEPOSIT_PCT_MIN = 10;
export const CAPACITY_DEPOSIT_PCT_MAX = 50;
export const CAPACITY_DURATION_MONTHS_MIN = 24;
export const CAPACITY_DURATION_MONTHS_MAX = 60;

/**
 * A SANITY bound on a typed amount, not a business rule (the architect's ruling of 31 October): the record has none, and 100 million FCFA a month is a slip of the keypad, not a salary. No refusal
 * sentence may present it as a limit of the product.
 */
export const CAPACITY_AMOUNT_MAX = 100_000_000;

/** The effort bands, the client's own wire values (`capacite.ts`, `EffortBand`): the dealer's band is the client's band for the same instalment and salary. */
export const CAPACITY_EFFORT_BANDS = ["confortable", "acceptable", "au-dela"] as const;

/** A rate as the policy grid writes it: dot-decimal, one decimal (`11.4`). */
const ONE_DECIMAL_RATE = /^\d{1,2}\.\d$/u;

/** `POST /api/v1/me/capacity-estimate`. Strict: an unknown key is a mismatch, the field named. */
export const capacityEstimateBodySchema = z.strictObject({
  /** Required, `> 0` (the record's refusal « Le salaire net doit être un nombre supérieur à zéro. »). */
  netSalaryAmount: z.number().int().min(1).max(CAPACITY_AMOUNT_MAX),
  /** Absent reads 0 (« Laissez vide s'il n'en a aucun »). */
  monthlyCommitmentsAmount: z.number().int().min(0).max(CAPACITY_AMOUNT_MAX).optional(),
  depositPct: z.number().int().min(CAPACITY_DEPOSIT_PCT_MIN).max(CAPACITY_DEPOSIT_PCT_MAX),
  durationMonths: z.number().int().min(CAPACITY_DURATION_MONTHS_MIN).max(CAPACITY_DURATION_MONTHS_MAX),
});

const buildAnswerSchemas = (object: typeof z.strictObject) => {
  const range = object({ min: z.number().int(), max: z.number().int() });
  const assumption = object({
    /** A stable key the screen may order by; the words are `label` and `value`. */
    key: z.string().min(1),
    label: z.string().min(1),
    /** Served words, drawn verbatim (« 10,4 % à 11,2 % », « 40 % du salaire net »). */
    value: z.string().min(1),
    /** Where the record keeps it, so the line is explainable (« simulation_settings v7 », « politique des banques actives »). */
    source: z.string().min(1),
  });
  const limits = object({
    depositPct: object({ min: z.number().int(), max: z.number().int() }),
    durationMonths: object({ min: z.number().int(), max: z.number().int() }),
  });
  const carWithin = object({
    vehicleId: z.uuid(),
    title: z.string().min(1),
    priceAmount: z.number().int(),
    /** The instalment of THIS car at the best and the worst grade across the panel (insurance included), computed as the range is. */
    monthlyEstimate: range,
    /** The client's own band for the worst-case instalment (`effortBand`); an estimate like the rest. */
    effortBand: z.enum(CAPACITY_EFFORT_BANDS),
    /** The band's words, the CLIENT's own (`client.eligibilite.bandeConfortable` / `bandeAcceptable` / `bandeAuDela`), served so no screen words a band. The cut-offs are Hakili's (35 % / 50 %) on every platform. */
    effortBandLabel: z.string().min(1),
    /** Salary less commitments less the instalment, best to worst (`resteAVivre`, never negative); nothing is stored. */
    resteAVivre: range,
  });
  /** What every state carries. `kind` draws the « ESTIMATION » chip. */
  const base = {
    kind: z.literal("estimation"),
    depositPct: z.number().int(),
    durationMonths: z.number().int(),
    /** The record's estimate notice, served and drawn verbatim. */
    caveat: z.string().min(1),
    assumptions: z.array(assumption),
    limits,
    /** Which settings row produced it, so an old estimate stays explainable. */
    settingsVersion: z.number().int(),
  };
  const figures = {
    /** The amount the range can finance, after the deposit is set aside; fees are NOT financed. */
    financeableAmount: range,
    /** Per month, insurance included: the comfort share to the bank's share, less the commitments. */
    monthlyAmount: range,
    /** The lowest and highest cessible share (`credit_policies.quotite_cessible_pct`) across the active panel: the range is computed bank by bank. */
    ceilingPct: z.number().int(),
    /** The same ceiling as a RANGE, since each active bank has its own cessible share: its lowest and its highest across the panel. */
    ceilingPctRange: object({ min: z.number().int(), max: z.number().int() }),
    /** Best to worst grade across the active panel, dot-decimal. */
    ratePct: object({ best: z.string().regex(ONE_DECIMAL_RATE), worst: z.string().regex(ONE_DECIMAL_RATE) }),
  };
  /** A car of the park that has NO surviving grade for its own amount (every grade's TAEG is over the ceiling): served by code, never as a figure. */
  const carWithoutRate = object({
    vehicleId: z.uuid(),
    title: z.string().min(1),
    priceAmount: z.number().int(),
    reason: z.literal("no_rate_within_ceiling"),
    /** The served sentence, PROPOSED for the dealer word batch (A-H, 2 November): a car is never dropped silently, nor counted only. */
    sentence: z.string().min(1),
  });
  const estimate = object({
    ...base,
    state: z.literal("estimate"),
    ...figures,
    cars: object({
      parkCount: z.number().int().min(1),
      within: z.array(carWithin).min(1),
      /** The cars placed by age but without a surviving grade at their own amount; the code is served, the words are A-H's to give. */
      withoutRate: z.array(carWithoutRate),
    }),
  });
  const noCarWithinRange = object({
    ...base,
    state: z.literal("no_car_within_range"),
    ...figures,
    /** `parkCount: 0` is the empty park; `> 0` is a park none of whose cars falls in the range or is accepted by an active bank. */
    cars: object({ parkCount: z.number().int().min(0), withoutRate: z.array(carWithoutRate) }),
  });
  const noCapacity = object({
    ...base,
    state: z.literal("no_capacity"),
    eligible: z.literal(false),
    /** What is left of the bank's share after the commitments; `0` when none. */
    capacityAmount: z.number().int().min(0),
    /** By how much the commitments exceed the bank's share; `0` at the boundary. */
    shortfallAmount: z.number().int().min(0),
  });
  return { estimate, noCarWithinRange, noCapacity };
};
const strictAnswer = buildAnswerSchemas(z.strictObject);
const looseAnswer = buildAnswerSchemas(z.looseObject);

export const capacityEstimateFiguresSchema = strictAnswer.estimate;
export const capacityEstimateNoCarSchema = strictAnswer.noCarWithinRange;
export const capacityEstimateNoCapacitySchema = strictAnswer.noCapacity;

/** `POST /api/v1/me/capacity-estimate`, the 200 answer: one of three states, told apart by `state`. */
export const capacityEstimateAnswerSchema = z.discriminatedUnion("state", [
  strictAnswer.estimate,
  strictAnswer.noCarWithinRange,
  strictAnswer.noCapacity,
]);

/** The dealer client's reading of the same answer: unknown keys tolerated at every level. */
export const capacityEstimateLenientReadSchema = z.discriminatedUnion("state", [
  looseAnswer.estimate,
  looseAnswer.noCarWithinRange,
  looseAnswer.noCapacity,
]);

export type CapacityEstimateBody = z.infer<typeof capacityEstimateBodySchema>;
export type CapacityEstimateAnswer = z.infer<typeof capacityEstimateAnswerSchema>;
